import React, { useMemo } from 'react';

import { InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import parse from 'html-react-parser';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { END } from 'redux-saga';
import { useDispatch, useSelector } from 'react-redux';

import { Accordion, Link, LoadMore } from 'atoms';
import { media_sm, media_md, colors, media_lg, transition, transitionPageVariants } from 'theme';
import { wrapper, actions, routes } from 'core';
import { faqs as selectors } from 'selectors';
import { SeoApi } from 'services';

export const getServerSideProps = wrapper.getServerSideProps((store) => async (context) => {
  const category = context.query.category?.toString() || 'all';
  store.dispatch(actions.faqs.putCurrentCategoryToStoreAction({ slug: category }));
  store.dispatch(actions.faqs.getFaqsRequestAction());
  store.dispatch(actions.faqs.getCategoriesRequestAction());
  store.dispatch(END);
  const [seo] = await Promise.all([SeoApi.getFaq(), store.sagaTask?.toPromise()]);
  return { props: { category, seo } };
});

export default function Faq({ category, seo }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const dispatch = useDispatch();
  const faqs = useSelector(selectors.getFaqs);
  const total = useSelector(selectors.getTotal);
  const categorys = useSelector(selectors.getCategories);

  const tabs = useMemo(
    () => [
      { label: 'Все', color: colors.violet, id: 'all', slug: 'all' },
      ...categorys.map(({ id, slug, name }) => ({ label: name, color: colors.violet, id, slug })),
    ],
    [categorys],
  );

  const handleLoadMore = () => {
    dispatch(actions.faqs.getFaqsRequestAction());
  };

  return (
    <>
      <Head>
        <title>
          {category === 'all' ? seo[0]?.title : categorys.find((item) => item.slug === category)?.seoTitle ?? 'belbet'}
        </title>
        <meta
          name="description"
          content={
            category === 'all'
              ? seo[0]?.description
              : categorys.find((item) => item.slug === category)?.seoDescription ?? 'belbet'
          }
        />
      </Head>

      <Title>Вопросы</Title>

      {tabs.length > 2 && (
        <Tabs>
          {tabs.map((item) => (
            <StyledLink
              href={{
                pathname: routes.faq,
                query: { category: item.slug },
              }}
              key={item.id}
              $content={item.label}
            >
              {item.label}
            </StyledLink>
          ))}
        </Tabs>
      )}

      <AnimatePresence initial={false}>
        <SlideMotion
          key={category}
          initial="slideIn"
          animate="default"
          exit="slideOut"
          variants={transitionPageVariants}
          transition={transition}
        >
          <AnimatePresence initial={false}>
            {faqs.map((faq) => (
              <React.Fragment key={faq.id}>
                <FaqMotion initial={{ y: '100vh' }} animate={{ y: 0 }} transition={transition}>
                  <Accordion
                    data={{
                      id: faq.id,
                      title: faq.question,
                      content: <FaqContent>{parse(faq.answer)}</FaqContent>,
                    }}
                  />
                </FaqMotion>
              </React.Fragment>
            ))}
          </AnimatePresence>
          {total > faqs.length && <LoadMore onClick={handleLoadMore}>ЕЩЕ ВОПРОСЫ</LoadMore>}
        </SlideMotion>
      </AnimatePresence>
    </>
  );
}

const Title = styled.h1`
  font-size: 24px;
  color: ${colors.mainText};

  ${media_sm} {
    font-size: 30px;
  }

  ${media_md} {
    display: none;
  }
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 40px;
  flex-wrap: wrap;
  /* max-width: max-content; */
  /* overflow-x: scroll; */
  justify-content: flex-start;
  align-items: center;

  /* &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none; */

  ${media_sm} {
    margin-bottom: 49px;
    margin-bottom: 39px;
  }
`;

const StyledLink = styled(Link)<{ $content: string }>`
  ${({ $content }) => css`
    margin-right: 40px;
    font-size: 18px;
    color: ${colors.violet};
    font-weight: 600;
    white-space: nowrap;
    margin-bottom: 10px;

    &:hover {
      color: ${colors.mainText};
      text-decoration: none;
    }

    &.active {
      color: ${colors.mainText};
      font-weight: 700;
    }

    /* for bold font-weight and windth */
    display: inline-flex;
    flex-direction: column;
    &::after {
      font-weight: 700;
      content: '${$content}';
      height: 0;
      visibility: hidden;
      overflow: hidden;
      user-select: none;
      pointer-events: none;
    }

    ${media_sm} {
      margin-right: 60px;
    }
  `}
`;

const SlideMotion = styled(motion.div)`
  width: 300px;

  @media (min-width: 320px) {
    width: 313px;
  }
  @media (min-width: 330px) {
    width: 320px;
  }

  ${media_sm} {
    width: 710px;
  }

  ${media_md} {
    width: 750px;
  }

  ${media_lg} {
    width: 1140px;
  }
`;

const FaqMotion = styled(motion.div)`
  padding: 0 0 40px 0;
`;

const FaqContent = styled.div`
  font-size: 14px;
  line-height: 21px;
  padding: 0 30px 30px 30px;

  & li {
    margin-left: 20px;
  }

  ${media_sm} {
    & li {
      margin-left: 30px;
    }
  }

  ${media_sm} {
    font-size: 18px;
    line-height: 27px;
    padding: 0 60px 30px 118px;
  }

  ${media_lg} {
    /* margin-top: -18px; */
    padding: 0 96px 43px 191px;
  }
`;
